import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { BRANDING } from '../config/branding';
import { SPORTS, SPORT_ICON_MAP, APP_STORE_LINKS, isAppRoute } from '../utils/constants';
import { auth } from '../services/Firebase';

const STATIC_LINKS = [
  { id: 'home', label: 'Home', to: '/', icon: 'fa-house' },
  { id: 'packages', label: 'App Access', to: '/packages', icon: 'fa-bolt' },
  { id: 'login', label: 'Log in', to: '/login', icon: 'fa-right-to-bracket' },
];

const Navbar = styled.nav`
  position: ${({ $fixed }) => $fixed ? 'fixed' : 'sticky'};
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  transition: background 0.4s ease, box-shadow 0.4s ease, border-color 0.4s ease;
  background: ${({ $dark }) =>
    $dark ? 'rgba(8,8,8,0.18)' : 'linear-gradient(135deg, #ffffff 0%, #f5f7fb 100%)'};
  backdrop-filter: ${({ $dark }) => $dark ? 'blur(22px) saturate(140%)' : 'none'};
  -webkit-backdrop-filter: ${({ $dark }) => $dark ? 'blur(22px) saturate(140%)' : 'none'};
  box-shadow: ${({ $dark }) =>
    $dark ? '0 1px 0 rgba(255,255,255,0.06)' : '0 8px 32px rgba(0,0,0,.08), 0 4px 16px rgba(0,0,0,.04)'};
  border-bottom: 1px solid ${({ $dark }) => $dark ? 'rgba(255,255,255,0.07)' : '#e2e8f0'};
`;

const NavInner = styled.div`
  width: 100%;
  padding: 0 30px;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 1rem;
  @media (max-width: 768px) { padding: 0 20px; height: 60px; }
`;

const BrandLink = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;

  .brand-icon {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    object-fit: contain;
    display: block;
    filter: drop-shadow(0 0 8px rgba(196,152,14,.45));
  }

  .brand-name {
    font-family: 'Poppins', sans-serif;
    font-weight: 800;
    font-size: 1rem;
    color: ${({ $dark }) => $dark ? '#f1f5f9' : '#1c2237'};
    white-space: nowrap;
    letter-spacing: -0.02em;
  }

  @media (max-width: 768px) {
    .brand-icon { width: 26px; height: 26px; }
    .brand-name { font-size: 0.88rem; }
  }
`;

const NavLinks = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.25rem;
  @media (max-width: 768px) { display: none; }
`;

const NavPill = styled.button`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.6rem 1.25rem;
  border-radius: 50px;
  font-size: 0.95rem;
  font-weight: 600;
  letter-spacing: 0.3px;
  border: none;
  cursor: pointer;
  white-space: nowrap;
  background: ${({ $active, $dark }) =>
    $active
      ? ($dark ? 'rgba(255,255,255,0.18)' : 'rgba(28, 34, 55, 1)')
      : 'transparent'};
  color: ${({ $active, $dark }) =>
    $dark ? '#f1f5f9' : ($active ? '#ffffff' : '#1c2237')};
  box-shadow: ${({ $active }) => ($active ? '0 2px 10px rgba(28,34,55,.25)' : 'none')};
  border: none;
  &:hover {
    background: ${({ $active, $dark }) =>
      $active
        ? ($dark ? 'rgba(255,255,255,0.22)' : 'rgba(28, 34, 55, 1)')
        : ($dark ? 'rgba(255,255,255,0.1)' : 'rgba(28,34,55,.07)')};
    color: ${({ $dark }) => $dark ? '#ffffff' : '#1c2237'};
  }
  i { font-size: 0.9rem; margin-right: 0.35rem; }
`;

const Hamburger = styled.button`
  margin-left: auto;
  display: none;
  background: none;
  border: none;
  color: ${({ $dark }) => $dark ? '#f1f5f9' : '#1c2237'};
  font-size: 1.4rem;
  padding: 0.4rem;
  cursor: pointer;
  @media (max-width: 768px) { display: flex; align-items: center; }
`;

const MobileNav = styled.div`
  display: ${({ $open }) => $open ? 'flex' : 'none'};
  flex-direction: column;
  gap: 0.35rem;
  padding: 0.75rem 1.25rem 1rem;
  border-top: 1px solid ${({ $dark }) => $dark ? 'rgba(255,255,255,0.08)' : '#e2e8f0'};
  background: ${({ $dark }) => $dark ? 'rgba(10,13,20,0.95)' : 'linear-gradient(135deg, #ffffff 0%, #f5f7fb 100%)'};
  backdrop-filter: ${({ $dark }) => $dark ? 'blur(18px)' : 'none'};
  @media (min-width: 769px) { display: none !important; }
`;

const MobilePill = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.7rem 1rem;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  text-align: left;
  background: ${({ $active, $dark }) =>
    $active ? ($dark ? 'rgba(255,255,255,0.15)' : '#1c2237') : 'transparent'};
  color: ${({ $active, $dark }) =>
    $dark ? '#f1f5f9' : ($active ? '#ffffff' : '#1c2237')};
  &:hover { background: ${({ $dark }) => $dark ? 'rgba(255,255,255,0.1)' : 'rgba(28,34,55,.07)'}; }
  i { font-size: 0.9rem; }
`;

const CreditPill = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.58rem 0.95rem;
  border-radius: 50px;
  font-size: 0.84rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  border: 1px solid ${({ $dark }) => $dark ? 'rgba(240,200,118,0.32)' : 'rgba(180,120,6,0.32)'};
  background: ${({ $dark }) => $dark ? 'rgba(240,200,118,0.12)' : 'rgba(180,120,6,0.08)'};
  color: ${({ $dark }) => $dark ? '#f0c876' : '#7c5200'};
  white-space: nowrap;
`;

const StoreLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  @media (max-width: 768px) { display: none; }
`;

const StoreLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  font-size: 1.05rem;
  text-decoration: none;
  border: 1px solid ${({ $dark }) => $dark ? 'rgba(255,255,255,0.18)' : '#e2e8f0'};
  background: ${({ $dark }) => $dark ? 'rgba(255,255,255,0.08)' : '#ffffff'};
  color: ${({ $dark }) => $dark ? '#f1f5f9' : '#1c2237'};
  transition: background 0.2s ease, color 0.2s ease;
  &:hover {
    background: ${({ $dark }) => $dark ? 'rgba(255,255,255,0.18)' : 'rgba(28,34,55,.07)'};
    color: ${({ $dark }) => $dark ? '#ffffff' : '#1c2237'};
  }
`;

const STORE_LINKS = [
  { id: 'ios', label: 'App Store', href: APP_STORE_LINKS.ios, icon: 'fa-apple' },
  { id: 'android', label: 'Google Play', href: APP_STORE_LINKS.android, icon: 'fa-google-play' },
];

const Header = () => {
  const history = useHistory();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const remainingCredits = useSelector(state => state.access.remainingCredits);
  const profile = useSelector(state => state.user.profile);
  const profileDisplayName = String(profile?.displayName || auth.currentUser?.displayName || '').trim() || 'Profile';
  const safeRemainingCredits = Number.isFinite(Number(remainingCredits)) ? Number(remainingCredits) : 0;

  const pathname = location.pathname;
  const isApp = isAppRoute(pathname);
  const isHome = pathname === '/';
  const isLogin = pathname.startsWith('/login');
  const currentSport = pathname.startsWith('/sports/') ? pathname.split('/sports/')[1] : null;
  // Header is dark when on the home page (pre-scroll) or always on the sports/app pages
  const isDark = isApp || isLogin || (isHome && !scrolled);

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    if (!isHome) { setScrolled(false); return; }
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [isHome]);

  const go = to => { setOpen(false); history.push(to); };

  return (
    <Navbar $dark={isDark}>
      <NavInner>
        <BrandLink $dark={isDark} onClick={() => go('/')}>
          <img className="brand-icon" src={BRANDING.iconPath} alt="" onError={e => { e.target.style.display = 'none'; }} />
          <span className="brand-name">{BRANDING.appName}</span>
        </BrandLink>

        {isApp && (
          <StoreLinks>
            {STORE_LINKS.map(link => (
              <StoreLink
                key={link.id}
                $dark={isDark}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Get the app on ${link.label}`}
                title={link.label}
              >
                <i className={`fab ${link.icon}`} />
              </StoreLink>
            ))}
          </StoreLinks>
        )}

        <NavLinks>
          {isApp ? (
            <>
              {SPORTS.map(s => (
                <NavPill
                  key={s}
                  $active={currentSport === s}
                  $dark={isDark}
                  onClick={() => go(`/sports/${s}`)}
                >
                  <i className={`fas ${SPORT_ICON_MAP[s]}`} />{s.toUpperCase()}
                </NavPill>
              ))}
              <CreditPill $dark={isDark}>
                <i className="fas fa-coins" />
                {`Credits: ${safeRemainingCredits}`}
              </CreditPill>
              <NavPill $active={pathname.startsWith('/settings') || pathname.startsWith('/profile')} $dark={isDark} onClick={() => go('/settings')}>
                <i className="fas fa-user" />
                {profileDisplayName}
              </NavPill>
            </>
          ) : (
            STATIC_LINKS.map(link => (
              <NavPill
                key={link.id}
                $active={link.to === '/' ? pathname === '/' : pathname.startsWith(link.to)}
                $dark={isDark}
                onClick={() => go(link.to)}
              >
                <i className={`fas ${link.icon}`} />
                {link.label}
              </NavPill>
            ))
          )}
        </NavLinks>

        <Hamburger $dark={isDark} onClick={() => setOpen(o => !o)} aria-label="Toggle menu">
          <i className={`fas ${open ? 'fa-times' : 'fa-bars'}`} />
        </Hamburger>
      </NavInner>

      <MobileNav $open={open} $dark={isDark}>
        {isApp ? (
          <>
            {SPORTS.map(s => (
                <MobilePill
                  key={s}
                  $active={currentSport === s}
                  $dark={isDark}
                  onClick={() => go(`/sports/${s}`)}
                >
                  <i className={`fas ${SPORT_ICON_MAP[s]}`} />{s.toUpperCase()}
                </MobilePill>
            ))}
            <MobilePill as="div" $dark={isDark} style={{ justifyContent: 'center', fontWeight: 800 }}>
              <i className="fas fa-coins" />
              {`Credits: ${safeRemainingCredits}`}
            </MobilePill>
            {STORE_LINKS.map(link => (
              <MobilePill
                key={link.id}
                as="a"
                $dark={isDark}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: 'none' }}
              >
                <i className={`fab ${link.icon}`} /> {link.label}
              </MobilePill>
            ))}
            <MobilePill $active={pathname.startsWith('/settings')} $dark={isDark} onClick={() => go('/settings')}>
              <i className="fas fa-user" /> {profileDisplayName}
            </MobilePill>
          </>
        ) : (
          STATIC_LINKS.map(link => (
            <MobilePill
              key={link.id}
              $active={link.to === '/' ? pathname === '/' : pathname.startsWith(link.to)}              $dark={isDark}              onClick={() => go(link.to)}
            >
              <i className={`fas ${link.icon}`} />
              {link.label}
            </MobilePill>
          ))
        )}
      </MobileNav>
    </Navbar>
  );
};

export default Header;
